import { SIDEBAR_FALLBACK_GROUP } from '../config/sidebarGroups';
import type { SidebarGroup } from '../config/sidebarGroups';

export interface SidebarMenuItem {
  key?: string | number;
  code?: unknown;
  path?: unknown;
  name?: unknown;
}

export interface SidebarGroupBucket<T> {
  group: SidebarGroup;
  items: T[];
}

function normalizePath(value: unknown): string {
  if (typeof value !== 'string') return '';
  const path = value.trim().split(/[?#]/, 1)[0];
  return path.startsWith('/') ? path.replace(/\/+$/, '') || '/' : '';
}

function normalizeName(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, '').toLowerCase() : '';
}

/**
 * 仅重排一级菜单：编码 > 最长路径前缀 > 名称，未命中的入口始终进入兜底组。
 * 保留菜单对象与 children 身份，权限树和组内顺序均不改变；空组不参与渲染。
 */
export function buildSidebarGroups<T extends SidebarMenuItem>(
  menus: readonly T[],
  groups: readonly SidebarGroup[] = [],
): SidebarGroupBucket<T>[] {
  const buckets: SidebarGroupBucket<T>[] = groups
    .filter((group) => !group.isFallback)
    .map((group) => ({ group, items: [] }));
  const fallback: SidebarGroupBucket<T> = {
    group: groups.find((group) => group.isFallback) || SIDEBAR_FALLBACK_GROUP,
    items: [],
  };

  for (const item of menus) {
    const code = typeof item.code === 'string' ? item.code : '';
    let target = code
      ? buckets.find(({ group }) => group.match?.codes?.includes(code))
      : undefined;

    if (!target) {
      const path = normalizePath(item.path) || normalizePath(item.key);
      let longestPrefix = -1;
      if (path) {
        for (const bucket of buckets) {
          for (const rule of bucket.group.match?.paths || []) {
            const prefix = normalizePath(rule);
            if (
              prefix &&
              prefix.length > longestPrefix &&
              (path === prefix || prefix === '/' || path.startsWith(`${prefix}/`))
            ) {
              target = bucket;
              longestPrefix = prefix.length;
            }
          }
        }
      }
    }

    if (!target) {
      const name = normalizeName(item.name);
      if (name) {
        target = buckets.find(({ group }) => group.match?.names?.some((rule) => {
          const normalizedRule = normalizeName(rule);
          return normalizedRule.length > 0 && name.includes(normalizedRule);
        }));
      }
    }

    (target || fallback).items.push(item);
  }

  return [...buckets, fallback].filter(({ items }) => items.length > 0);
}
